import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, addMinutes } from "date-fns";
import toast from "react-hot-toast";
import clsx from "clsx";
import {
  HiOutlineSearch,
  HiOutlineX,
  HiOutlineExclamation,
  HiOutlineCheckCircle,
  HiOutlineExternalLink,
} from "react-icons/hi";
import { Modal, Button, Input, Textarea, Select, SearchableSelect, Spinner } from "../../components/UI";
import { visitsAPI, leadsAPI, usersAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { VISIT_TYPES, DURATION_OPTIONS, fullName, leadAddress } from "./visitConstants";

// Next half hour from now, used as the default start
const defaultStart = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return d;
};

const toLocalDate = (date, time) => (date && time ? new Date(`${date}T${time}`) : null);

/**
 * Schedule / edit / reschedule a visit.
 *
 * mode: 'create' | 'edit' | 'reschedule'
 * visit: the visit being edited or rescheduled
 * lead: optional preselected lead (from lead detail) — { _id, name, phone, address }
 * previousVisit: completed visit to book a repeat visit after (create mode;
 *   prefills customer, employee, type and place, and links the two)
 */
const VisitModal = ({ isOpen, onClose, mode = "create", visit, previousVisit, lead: presetLead, onSaved }) => {
  const { isAdmin, user } = useAuth();
  const isReschedule = mode === "reschedule";
  const isEdit = mode === "edit";

  const [lead, setLead] = useState(null);
  const [assignedTo, setAssignedTo] = useState("");
  const [type, setType] = useState("site_visit");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("60");
  const [address, setAddress] = useState("");
  const [meetLink, setMeetLink] = useState("");
  const [title, setTitle] = useState("");
  const [agenda, setAgenda] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [clash, setClash] = useState(null); // { available, message }
  const [checking, setChecking] = useState(false);

  // Reset the form whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const source = visit || previousVisit || {};
    // Edit and reschedule open on the visit's current slot so the user can see
    // what they are moving; a new or repeat visit starts from the next half hour.
    const start = visit?.scheduledStart ? new Date(visit.scheduledStart) : defaultStart();
    const mins = source.scheduledStart
      ? Math.round((new Date(source.scheduledEnd) - new Date(source.scheduledStart)) / 60000)
      : 60;
    const initialLead = source.leadId && typeof source.leadId === "object" ? source.leadId : presetLead || null;
    setLead(initialLead);
    setAssignedTo(
      String(source.assignedTo?._id || source.assignedTo || presetLead?.assignedTo?._id || presetLead?.assignedTo || (isAdmin ? "" : user?._id) || "")
    );
    setType(source.type || "site_visit");
    setDate(format(start, "yyyy-MM-dd"));
    setTime(format(start, "HH:mm"));
    setDuration(String(mins));
    setAddress(source.address ?? leadAddress(initialLead));
    setMeetLink(source.meetLink || "");
    setTitle(source.title || "");
    setAgenda(visit?.agenda || "");
    setReason("");
    setErrors({});
    setClash(null);
  }, [isOpen, visit, previousVisit, presetLead, isReschedule, isAdmin, user]);

  const { data: users = [] } = useQuery({
    queryKey: ["users", "visit-assignees"],
    queryFn: async () => {
      const res = await usersAPI.getAll({ limit: 500 });
      return (res.data?.data || []).filter((u) => u.isActive !== false);
    },
    enabled: isOpen && isAdmin,
    staleTime: 60000,
  });

  const userOptions = useMemo(
    () => users.map((u) => ({ value: String(u._id), label: `${fullName(u)}${u.role ? ` (${u.role})` : ""}` })),
    [users]
  );

  const durationOptions = useMemo(
    () =>
      DURATION_OPTIONS.some((o) => o.value === duration)
        ? DURATION_OPTIONS
        : [...DURATION_OPTIONS, { value: duration, label: `${duration} min` }],
    [duration]
  );

  const start = toLocalDate(date, time);
  const end = start ? addMinutes(start, parseInt(duration, 10) || 0) : null;
  const effectiveAssignee = isAdmin ? assignedTo : user?._id;

  // Live clash check (debounced)
  useEffect(() => {
    if (!isOpen || !start || !effectiveAssignee) {
      setClash(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await visitsAPI.checkSlot({
          scheduledStart: start.toISOString(),
          scheduledEnd: end.toISOString(),
          assignedTo: effectiveAssignee,
          excludeId: visit?._id,
        });
        setClash(res.data?.data || null);
      } catch {
        setClash(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, date, time, duration, effectiveAssignee, visit?._id]);

  const validate = () => {
    const e = {};
    if (!lead?._id) e.lead = "Select a customer";
    if (isAdmin && !assignedTo && !isReschedule) e.assignedTo = "Select an employee";
    if (!date) e.date = "Select a date";
    if (!time) e.time = "Select a time";
    if (start && start.getTime() < Date.now() - 5 * 60000) e.time = "Time is in the past";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    if (clash && !clash.available) {
      toast.error(clash.message || "Time slot clashes with another visit");
      return;
    }
    const slot = { scheduledStart: start.toISOString(), scheduledEnd: end.toISOString() };
    setSaving(true);
    try {
      let res;
      if (isReschedule) {
        res = await visitsAPI.reschedule(visit._id, { ...slot, reason });
      } else {
        const payload = {
          ...slot,
          type,
          title,
          agenda,
          address,
          meetLink: type === "online_meeting" ? meetLink : "",
          ...(isAdmin ? { assignedTo } : {}),
        };
        res = isEdit
          ? await visitsAPI.update(visit._id, payload)
          : await visitsAPI.create({ ...payload, leadId: lead._id, previousVisitId: previousVisit?._id });
      }
      toast.success(res.data?.message || "Saved");
      onSaved?.(res.data?.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save visit");
    } finally {
      setSaving(false);
    }
  };

  const heading = isReschedule
    ? "Reschedule Visit"
    : isEdit
    ? "Edit Visit"
    : previousVisit
    ? "Schedule Next Visit"
    : "Schedule Visit / Meeting";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={heading} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-secondary-700">
            Customer<span className="text-red-500">*</span>
          </label>
          {lead ? (
            <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 bg-secondary-50">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-secondary-900 truncate">{lead.name}</p>
                <p className="text-xs text-secondary-500 truncate">
                  {[lead.phone, lead.companyName].filter(Boolean).join(" · ")}
                </p>
              </div>
              {mode === "create" && !presetLead && !previousVisit && (
                <button
                  type="button"
                  onClick={() => setLead(null)}
                  className="p-1 rounded-lg text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100"
                  aria-label="Change customer"
                >
                  <HiOutlineX className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <LeadPicker
              onPick={(l) => {
                setLead(l);
                if (!address) setAddress(leadAddress(l));
                if (isAdmin && !assignedTo && l.assignedTo) {
                  setAssignedTo(String(l.assignedTo?._id || l.assignedTo));
                }
              }}
              error={errors.lead}
            />
          )}
        </div>

        {/* Employee (admins/managers only) */}
        {isAdmin && !isReschedule && (
          <SearchableSelect
            label="Employee"
            required
            options={userOptions}
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="Select employee"
            searchPlaceholder="Search employee..."
            error={errors.assignedTo}
          />
        )}

        {/* Type */}
        {!isReschedule && (
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {VISIT_TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    aria-pressed={active}
                    className={clsx(
                      "flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all",
                      active
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-secondary-200 text-secondary-600 hover:border-secondary-300"
                    )}
                  >
                    <Icon className="w-5 h-5" style={{ color: active ? undefined : t.color }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Slot */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label="Date"
            required
            type="date"
            value={date}
            min={format(new Date(), "yyyy-MM-dd")}
            onChange={(e) => setDate(e.target.value)}
            error={errors.date}
          />
          <Input
            label="Start time"
            required
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            error={errors.time}
          />
          <Select
            label="Duration"
            options={durationOptions}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>

        {/* Clash status */}
        {start && effectiveAssignee && (
          <div
            className={clsx(
              "flex items-start gap-2 px-3 py-2 rounded-lg text-xs font-medium",
              checking
                ? "bg-secondary-50 text-secondary-500"
                : clash?.available === false
                ? "bg-danger-50 text-danger-700"
                : "bg-success-50 text-success-700"
            )}
            role="status"
          >
            {checking ? (
              <>
                <Spinner size="sm" /> Checking availability...
              </>
            ) : clash?.available === false ? (
              <>
                <HiOutlineExclamation className="w-4 h-4 flex-shrink-0 mt-px" /> {clash.message}
              </>
            ) : (
              <>
                <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0 mt-px" />
                Free from {format(start, "hh:mm a")} to {format(end, "hh:mm a")}
              </>
            )}
          </div>
        )}

        {isReschedule ? (
          <Textarea
            label="Reason (optional)"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this visit being moved?"
          />
        ) : (
          <>
            {type === "online_meeting" ? (
              <div className="space-y-1.5">
                <Input
                  label="Google Meet link"
                  value={meetLink}
                  onChange={(e) => setMeetLink(e.target.value)}
                  placeholder="https://meet.google.com/abc-defg-hij"
                />
                <button
                  type="button"
                  onClick={() => window.open("https://meet.google.com/new", "_blank", "noopener,noreferrer")}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  <HiOutlineExternalLink className="w-3.5 h-3.5" />
                  Create a new Meet link, then paste it here
                </button>
              </div>
            ) : (
              <Textarea
                label="Address"
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Customer site / office address"
              />
            )}
            <Input
              label="Title (optional)"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Site inspection, Product demo"
            />
            <Textarea
              label="Agenda / notes (optional)"
              rows={2}
              value={agenda}
              maxLength={2000}
              onChange={(e) => setAgenda(e.target.value)}
            />
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-secondary-100">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={clash?.available === false}>
            {isReschedule ? "Reschedule" : isEdit ? "Save Changes" : "Schedule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Search-as-you-type lead picker
const LeadPicker = ({ onPick, error }) => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const { data: leads = [], isFetching } = useQuery({
    queryKey: ["visit-lead-search", debounced],
    queryFn: async () => {
      const res = await leadsAPI.getAll({ search: debounced, limit: 20 });
      return res.data?.data || [];
    },
    enabled: open && debounced.length >= 2,
    staleTime: 30000,
  });

  return (
    <div ref={boxRef} className="relative">
      <Input
        icon={<HiOutlineSearch className="w-4 h-4" />}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search lead by name, phone or company"
        error={error}
        aria-label="Search customer"
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-secondary-200 rounded-xl shadow-lg">
          {isFetching ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-secondary-500">
              <Spinner size="sm" /> Searching...
            </div>
          ) : leads.length === 0 ? (
            <p className="px-3 py-3 text-sm text-secondary-500">No leads found</p>
          ) : (
            leads.map((l) => (
              <button
                key={l._id}
                type="button"
                onClick={() => {
                  onPick(l);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-primary-50 border-b border-secondary-50 last:border-0"
              >
                <p className="text-sm font-semibold text-secondary-900 truncate">{l.name}</p>
                <p className="text-xs text-secondary-500 truncate">
                  {[l.phone, l.companyName, fullName(l.assignedTo)].filter(Boolean).join(" · ")}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default VisitModal;
